import { MouseEvent, useState } from 'react';

import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grow,
  Popper,
  Typography,
  makeStyles,
  styled,
} from '@material-ui/core';

import { RiMailLine } from '@remixicon/react';
import { differenceInDays } from 'date-fns';

import { formatDate } from '@spotify/confidence-core-react';
import {
  AdminV1PlanState,
  AdminV1PlanType,
  getTypeOrNull,
  useAccountSetupQuery,
} from '@spotify/confidence-graphql';

const CONTACT_EMAIL = 'jsoderholm@spotify.com';

const useStyles = makeStyles(theme => ({
  popperCard: {
    marginTop: theme.spacing(1),
    maxWidth: 430,
  },
  container: {
    marginBottom: theme.spacing(3),
  },
  cardActions: {
    justifyContent: 'flex-start',
    padding: 0,
  },
}));

const baseChipStyles = {
  margin: 0,
};

const ActiveTrialChip = styled(Chip)(({ theme }) => ({
  ...baseChipStyles,
  background: theme.palette.success.main,
  color: theme.palette.success.contrastText,
}));

const EndedTrialChip = styled(Chip)(({ theme }) => ({
  ...baseChipStyles,
  background: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
}));

export const TrialChip = () => {
  const classes = useStyles();
  const { data } = useAccountSetupQuery({ fetchPolicy: 'cache-first' });

  const currentAccount = getTypeOrNull(data?.currentAccount);
  const planState = currentAccount?.planState;
  const planType = currentAccount?.planType;
  const planValidUntilTime = currentAccount?.planValidUntilTime;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const isActiveTrial =
    planType === AdminV1PlanType.Trial && planState === AdminV1PlanState.Active;
  const isExpiredTrial =
    planType === AdminV1PlanType.Trial &&
    planState === AdminV1PlanState.Expired;

  if (!isActiveTrial && !isExpiredTrial) {
    return null;
  }

  const trialEndDate = planValidUntilTime
    ? new Date(planValidUntilTime)
    : new Date();
  const trialEndDateFormatted = formatDate(trialEndDate);
  const remainingDays = Math.max(0, differenceInDays(trialEndDate, new Date()));
  const remainingDaysText = remainingDays === 1 ? 'day' : 'days';

  const ChipComponent = isActiveTrial ? ActiveTrialChip : EndedTrialChip;
  const chipLabel = isActiveTrial ? (
    <span>
      <strong>Active trial</strong> · {remainingDays} {remainingDaysText} left
    </span>
  ) : (
    <span>
      <strong>Trial ended</strong> · {trialEndDateFormatted}
    </span>
  );

  return (
    <div onMouseLeave={handlePopoverClose}>
      <ButtonBase
        component={ChipComponent}
        onMouseEnter={handlePopoverOpen}
        label={chipLabel}
        size="small"
      />
      <Popper open={open} anchorEl={anchorEl} transition disablePortal>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Card className={classes.popperCard}>
              <CardContent>
                <Box className={classes.container}>
                  <Typography paragraph variant="h6">
                    {isActiveTrial ? 'Active trial' : 'Trial ended'}
                  </Typography>
                  <Typography paragraph variant="body2">
                    {isActiveTrial ? (
                      <>
                        Your Confidence trial is active and ends on{' '}
                        <Box component="strong" color="secondary.main">
                          {trialEndDateFormatted}
                        </Box>
                        .
                      </>
                    ) : (
                      <>
                        Your Confidence trial ended on{' '}
                        <Box component="strong" color="text.warning">
                          {trialEndDateFormatted}
                        </Box>
                        .
                      </>
                    )}
                  </Typography>
                  <Typography paragraph variant="body2">
                    {isActiveTrial ? (
                      <>
                        Want to learn more about Confidence or discuss your
                        needs?
                      </>
                    ) : (
                      <>Want to continue using Confidence?</>
                    )}
                  </Typography>
                </Box>
                <CardActions className={classes.cardActions}>
                  <Button
                    variant="outlined"
                    component="a"
                    href={`mailto:${CONTACT_EMAIL}`}
                    onClick={handlePopoverClose}
                    size="small"
                    startIcon={<RiMailLine size={16} />}
                  >
                    Contact us
                  </Button>
                </CardActions>
              </CardContent>
            </Card>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
